import { useState } from "react";
import {
    Box,
    Collapse,
    Divider,
    IconButton,
    Paper,
    Typography,
    Button,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { colors } from "@components/ui/colors";

interface PriceBreakdownRow {
    label: string;
    value: string;
}

interface PriceBreakdownCardProps {
    pricingRows: PriceBreakdownRow[];
    pricingExtra?: PriceBreakdownRow[];
}

export const PriceBreakdownCard = ({
    pricingRows,
    pricingExtra = [],
}: PriceBreakdownCardProps) => {
    const [showPricing, setShowPricing] = useState(false);

    const hasExtraRows = pricingExtra.length > 0;

    const togglePricing = () => {
        if (!hasExtraRows) return;
        setShowPricing((value) => !value);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                border: `1.5px solid ${colors.gray[100]}`,
                borderRadius: "16px",
                p: 2.25,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1.75,
                }}
            >
                <Typography
                    sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: colors.gray[500],
                    }}
                >
                    Price breakdown
                </Typography>

                {hasExtraRows && (
                    <IconButton size="small" onClick={togglePricing}>
                        <KeyboardArrowDownIcon
                            sx={{
                                color: colors.orange[500],
                                transform: showPricing ? "rotate(180deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                                fontSize: 20,
                            }}
                        />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {pricingRows.map((row, index) => (
                    <Box key={`${row.label}-${index}`}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                py: 1,
                                gap: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: colors.gray[400],
                                    fontWeight: 500,
                                }}
                            >
                                {row.label}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: colors.gray[500],
                                    textAlign: "right",
                                }}
                            >
                                {row.value}
                            </Typography>
                        </Box>

                        {index < pricingRows.length - 1 && (
                            <Divider sx={{ borderColor: colors.gray[50] }} />
                        )}
                    </Box>
                ))}

                {hasExtraRows && (
                    <Collapse in={showPricing} timeout="auto" unmountOnExit>
                        {pricingExtra.map((row, index) => (
                            <Box key={`${row.label}-${index}`}>
                                <Divider sx={{ borderColor: colors.gray[50] }} />

                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        py: 1,
                                        gap: 2,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: colors.gray[400],
                                            fontWeight: 500,
                                        }}
                                    >
                                        {row.label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: 13,
                                            fontWeight: 700,
                                            color: colors.gray[500],
                                            textAlign: "right",
                                        }}
                                    >
                                        {row.value}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Collapse>
                )}
            </Box>

            {hasExtraRows && (
                <Button
                    endIcon={
                        <ArrowForwardIcon
                            sx={{
                                fontSize: "14px !important",
                                transform: showPricing ? "rotate(90deg)" : "rotate(0deg)",
                                transition: "transform 0.2s",
                            }}
                        />
                    }
                    onClick={togglePricing}
                    sx={{
                        mt: 1.5,
                        p: 0,
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 700,
                        color: colors.orange[500],
                        "&:hover": {
                            bgcolor: "transparent",
                            color: colors.orange[700],
                        },
                    }}
                >
                    {showPricing ? "Hide pricing details" : "View full pricing"}
                </Button>
            )}
        </Paper>
    );
};